import { api } from '../lib/api';

export interface PollenMeta {
  apiaries: Array<{ id: number; name: string }>;
  sites: Array<{ id: number; name: string }>;
  bulkProducts: Array<{ id: number; name: string }>;
  packagingProducts: Array<{ id: number; name: string; netWeightG: number | null }>;
  statuses: Array<{ id: number; label: string }>;
  containers: Array<{
    id: number;
    containerCode: string;
    productId: number;
    productName: string;
    siteId: number;
    siteName: string;
    statusItemId: number | null;
    statusLabel: string | null;
    remainingKg: number;
  }>;
}

export interface PollenBatchRow {
  id: number;
  containerCode: string;
  productId: number;
  productName: string;
  siteId: number;
  siteName: string;
  apiarySiteId: number | null;
  apiaryName: string | null;
  apiaries: Array<{ id: number; name: string }>;
  statusItemId: number | null;
  statusLabel: string | null;
  notes: string | null;
  batchDate: string;
  originType: string;
  originLabel: string;
  supplierName: string | null;
  harvestId: number | null;
  goodsReceiptId: number | null;
  remainingKg: number;
}

export interface PollenBatchDetails extends PollenBatchRow {
  harvestQtyKg: number | null;
  harvestLedgerTxId: number | null;
  packings: Array<{
    id: number;
    packingCode: string;
    packedAt: string;
    status: 'DRAFT' | 'POSTED';
    targetSiteId: number;
    targetSiteName: string;
    productId: number;
    productName: string;
    qtyUnits: number;
    unitWeightG: number;
    totalKg: number;
    batchCode: string;
  }>;
}

export interface PollenPackingRow {
  id: number;
  packingCode: string;
  packedAt: string;
  status: 'DRAFT' | 'POSTED';
  notes: string | null;
  sourceContainerId: number;
  sourceContainerCode: string;
  sourceSiteId: number;
  sourceSiteName: string;
  targetSiteId: number;
  targetSiteName: string;
  productId: number;
  productName: string;
  qtyUnits: number;
  unitWeightG: number;
  totalKg: number;
  batchCode: string;
}

export interface PollenPackingDetails {
  id: number;
  packingCode: string;
  packedAt: string;
  status: 'DRAFT' | 'POSTED';
  notes: string | null;
  consumeTxId: number | null;
  produceTxId: number | null;
  sourceContainerId: number;
  sourceContainerCode: string;
  sourceSiteId: number;
  sourceSiteName: string;
  targetSiteId: number;
  targetSiteName: string;
  lines: Array<{
    id: number;
    productId: number;
    productName: string;
    qtyUnits: number;
    unitWeightG: number;
    totalKg: number;
    batchCode: string;
  }>;
}

export const pollenApi = {
  meta: () => api.get<PollenMeta>('/pollen/meta'),
  batches: (year: number) => api.get<{ batches: PollenBatchRow[] }>(`/pollen/batches?year=${year}`),
  batch: (id: number) => api.get<PollenBatchDetails>(`/pollen/batches/${id}`),
  createHarvest: (payload: {
    harvestDate: string;
    apiarySiteIds: number[];
    productId: number;
    qtyKg: number;
    notes?: string;
  }) => api.post<{ id: number; containerId: number; batchCode: string }>('/pollen/harvests', payload),
  updateBatchStatus: (id: number, payload: { statusItemId: number; notes?: string }) =>
    api.patch<{ ok: boolean }>(`/pollen/batches/${id}/status`, payload),
  packings: (year: number) => api.get<{ packings: PollenPackingRow[] }>(`/pollen/packings?year=${year}`),
  packing: (id: number) => api.get<PollenPackingDetails>(`/pollen/packings/${id}`),
  createPacking: (payload: {
    packedAt: string;
    sourceContainerId: number;
    targetSiteId: number;
    productId: number;
    qtyUnits: number;
    notes?: string;
  }) => api.post<{ id: number; packingCode: string }>('/pollen/packings', payload),
  postPacking: (id: number) => api.post<{ ok: boolean; consumeTxId: number; produceTxId: number }>(`/pollen/packings/${id}/post`),
};
